import { useEventCallback } from '@mui/material';
import { Fragment, ReactElement, useState } from 'react';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import FilterResearchTypePractice from 'practice/components/type-practice/research/filter';
import TableTypePractice from 'practice/components/type-practice/research/table-type-practice';
import { validationSchema } from 'practice/constants/research-type-practice-schema';
import ListHeader from 'shell/components/shared/list-header';
import { useNavigate } from 'react-router-dom';
import { useActionAuthorization } from 'shell/hooks/use-action-authorization';
import { PracticeAuthorizations } from 'shell/authorizations/practice';
import { Form, Formik } from 'formik';
import { ResearchTypePracticeCrubs } from 'practice/constants/type-practice-crubs';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponseList } from 'shell/models/http-response';
import { TypePracticeData } from 'shell/models/type-practice';
import { AxiosResponse } from 'axios';
import { __PRACTICE_API_URL__ } from 'shell/constants/environment';
import { AlertModal } from 'system/components/shared/delete-alert/types';
import DeleteAlertModal from 'system/components/shared/delete-alert';
import DeleteSuccessModal from 'system/components/shared/delete-success';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { transformDataset } from './utils';
import {
  BodyResearchTypePractice,
  ResearchTypePracticeVM,
} from 'practice/view-models/research-type-practice';

export const authsObj = Object.freeze({
  read: [PracticeAuthorizations.RicercaTipoPratica],
  create: [PracticeAuthorizations.NuovoTipoPratica],
  edit: [PracticeAuthorizations.ModificaTipoPratica],
  delete: [PracticeAuthorizations.EliminaTipoPratica],
});

const ResearchTypePractice = (): ReactElement => {
  const navigate = useNavigate();

  const [typePractice, getTypePractice] = useHttpFetch<
    HttpResponseList<TypePracticeData>,
    Array<BodyResearchTypePractice>
  >(`${__PRACTICE_API_URL__}/pratiche/tipo-pratica`, {
    onBootstrap: false,
    options: {
      errorCatching: true,
      method: 'GET',
      loading: true,
      data: {},
    },
    transformer: (response: AxiosResponse<HttpResponseList<TypePracticeData>>) => {
      const { data } = response;
      return data?.payload?.list?.map((item) => ({
        code: item.key?.tipoPratica || '',
        description: item.descrizioneTipoPratica || '',
        descriptionBrief: item.descrizioneBreveTipoPratica || '',
        initiative: item.tipoIniziativa || '',
        measure: item.unitaMisuraTempo || '',
        register: item.tipoRegistro || '',
        timeMax: item.tempoMax || undefined,
        timeStandard: item.tempoStandard || undefined,
      }));
    },
  });

  const onSubmitFilter = useEventCallback((values: ResearchTypePracticeVM) => {
    const queryString = transformDataset(values);

    getTypePractice({
      params: {},
      paramsSerializer: () => queryString,
    });
  });

  const auths = useActionAuthorization(authsObj);

  const deletePractice = useHttpClient(`${__PRACTICE_API_URL__}/pratiche/tipo-pratica`, {
    options: { method: 'DELETE' },
  });

  const [deleteAlertModal, setDeleteAlertModal] = useState<AlertModal<BodyResearchTypePractice>>({
    element: null,
    open: false,
  });

  const [deleteSuccessModal, setDeleteSuccessModal] = useState<
    AlertModal<BodyResearchTypePractice>
  >({
    element: null,
    open: false,
  });

  return (
    <Fragment>
      <Breadcrumbs items={ResearchTypePracticeCrubs} />
      <ListHeader
        addBtnAriaLabel="Aggiungi nuovo tipo pratica"
        addBtnLabel="Aggiungi nuovo tipo pratica"
        canAdd={auths.create}
        onAddBtnClick={() => {
          navigate('/pratiche/ricerca-tipo-pratica/nuovo');
        }}
        title="Ricerca Tipo Pratica"
      />
      <div className="circle" />
      <Formik
        initialValues={{
          filter: { code: '', typeRegister: '', description: '', typeInitiative: '', orders: {} },
        }}
        onSubmit={onSubmitFilter}
        validationSchema={validationSchema}
      >
        <Form>
          <FilterResearchTypePractice />
          {typePractice ? (
            <TableTypePractice
              activeDelete={auths.delete}
              activeEdit={auths.edit}
              typePratice={typePractice.data as Array<BodyResearchTypePractice>}
              setDeleteAlertModal={setDeleteAlertModal}
              getTypePractice={getTypePractice}
            />
          ) : undefined}
        </Form>
      </Formik>
      <DeleteAlertModal
        title="Cancellazione Tipo Pratica"
        message="Confermare la cancellazione?"
        open={deleteAlertModal.open}
        onClose={() => setDeleteAlertModal({ open: false, element: null })}
        onYes={() => {
          (
            deletePractice({
              url: `/${deleteAlertModal.element?.code}`,
            }) as ResponsePromise<void>
          ).then(() => {
            setDeleteSuccessModal({ open: true, element: deleteAlertModal.element });
            setDeleteAlertModal({ open: false, element: null });
          });
        }}
        onNo={() => setDeleteAlertModal({ open: false, element: null })}
      />
      <DeleteSuccessModal
        open={deleteSuccessModal.open}
        title="Cancellazione Tipo Pratica"
        message={`La cancellazione del tipo pratica ${deleteSuccessModal.element?.code} è avvenuta con successo.`}
        onClose={() => {
          onSubmitFilter({
            filter: { code: '', typeRegister: '', description: '', typeInitiative: '', orders: {} },
          });
          setDeleteSuccessModal({ open: false, element: null });
        }}
        onOk={() => {
          onSubmitFilter({
            filter: { code: '', typeRegister: '', description: '', typeInitiative: '', orders: {} },
          });
          setDeleteSuccessModal({ open: false, element: null });
        }}
      />
    </Fragment>
  );
};

ResearchTypePractice.displayName = 'Type Practice Register Page component';

export default ResearchTypePractice;
